import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaRobot, FaPaperPlane, FaSpinner } from "react-icons/fa";
import { askGeminiAboutTicket } from "../../api/overviewApi";

/**
 * ChatDrawer – sliding right-side panel for AI ticket chat.
 *
 * Props:
 *   ticket   {Object|null}  – ticket object; null = closed
 *   onClose  {Function}     – called when drawer should close
 *   isDark   {boolean}
 */
const ChatDrawer = ({ ticket, onClose, isDark }) => {
  const [messages, setMessages]     = useState([]);
  const [history, setHistory]       = useState([]);
  const [input, setInput]           = useState("");
  const [sending, setSending]       = useState(false);
  const [initializing, setInitializing] = useState(false);
  const bottomRef                   = useRef(null);
  const inputRef                    = useRef(null);
  const open                        = !!ticket;

  // ── Colour tokens ─────────────────────────────────────────────────────────
  const bg        = isDark ? "rgba(18,18,30,0.98)"   : "#ffffff";
  const surface   = isDark ? "rgba(255,255,255,0.03)" : "#f8f8fc";
  const border    = isDark ? "rgba(255,255,255,0.08)" : "#eaeaf5";
  const textMain  = isDark ? "#f0f0f8"               : "#18182e";
  const textMute  = isDark ? "rgba(255,255,255,0.35)" : "#9090b0";
  const accent    = "#6366f1";

  // ── Reset + auto-initialize when ticket changes ───────────────────────────
  useEffect(() => {
    if (!ticket) return;

    setMessages([]);
    setHistory([]);
    setInput("");
    setSending(false);
    setInitializing(true);

    // Build an opening prompt so Gemini summarises the ticket immediately
    const openingPrompt =
      `Please analyze this ticket and give me a concise summary: what likely caused the issue, ` +
      `why it may have gone unresponded for ${ticket.days_unresponded} day${ticket.days_unresponded !== 1 ? "s" : ""}, ` +
      `and the top 2–3 recommended next actions for the support team.`;

    askGeminiAboutTicket(ticket, [], openingPrompt)
      .then((result) => {
        const aiText = result.reply || result.error || "Unable to analyze this ticket right now.";
        const isErr  = !!result.error;
        setMessages([{ role: "ai", text: aiText, isError: isErr }]);
        setHistory([
          { role: "user",  parts: [{ text: openingPrompt }] },
          { role: "model", parts: [{ text: aiText }] },
        ]);
      })
      .catch(() => {
        setMessages([{ role: "ai", text: "⚠️ Could not reach the AI assistant. Check your GEMINI_API_KEY setting.", isError: true }]);
      })
      .finally(() => {
        setInitializing(false);
        setTimeout(() => inputRef.current?.focus(), 80);
      });
  }, [ticket?.id]);

  // ── Auto-scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, initializing]);

  // ── Send a follow-up message ──────────────────────────────────────────────
  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || sending || initializing) return;

    setMessages(prev => [...prev, { role: "user", text }]);
    setInput("");
    setSending(true);

    const newHistory = [...history, { role: "user", parts: [{ text }] }];
    const result     = await askGeminiAboutTicket(ticket, history, text);

    if (result.reply) {
      setMessages(prev => [...prev, { role: "ai", text: result.reply }]);
      setHistory([...newHistory, { role: "model", parts: [{ text: result.reply }] }]);
    } else {
      setMessages(prev => [...prev, { role: "ai", text: `⚠️ ${result.error || "Something went wrong."}`, isError: true }]);
    }
    setSending(false);
  }, [input, sending, initializing, ticket, history]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // ── Minimal markdown bold renderer ───────────────────────────────────────
  const renderText = (text) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={i} style={{ fontWeight: 700 }}>{p.slice(2, -2)}</strong>
        : <span key={i}>{p}</span>
    );

  const daysBadgeColor = ticket
    ? ticket.days_unresponded >= 14 ? "#dc2626" : "#f59e0b"
    : "#6b7280";

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.38)",
              zIndex: 1200,
              backdropFilter: "blur(2px)",
            }}
          />

          {/* Drawer */}
          <motion.div
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            style={{
              position: "fixed", top: 0, right: 0, bottom: 0,
              width: 460, maxWidth: "95vw",
              background: bg,
              borderLeft: `1px solid ${border}`,
              boxShadow: "-8px 0 40px rgba(0,0,0,0.18)",
              zIndex: 1210,
              display: "flex", flexDirection: "column",
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* ── Header ── */}
            <div style={{
              padding: "14px 16px",
              borderBottom: `1px solid ${border}`,
              background: isDark ? "rgba(255,255,255,0.02)" : "#fafafe",
              display: "flex", alignItems: "center", gap: 10,
              flexShrink: 0,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9,
                background: "rgba(99,102,241,0.12)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <FaRobot size={14} color={accent} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: textMain }}>
                  AI Support Assistant
                </div>
                <div style={{ fontSize: 11, color: textMute, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {ticket?.ticket_number || "#" + ticket?.id} · {ticket?.queue}
                </div>
              </div>
              <button
                onClick={onClose}
                style={{ background: "none", border: "none", cursor: "pointer", color: textMute, padding: 4, borderRadius: 6, display: "flex" }}
                onMouseEnter={e => { e.currentTarget.style.color = textMain; e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "#f0f0f8"; }}
                onMouseLeave={e => { e.currentTarget.style.color = textMute; e.currentTarget.style.background = "none"; }}
              >
                <FaTimes size={14} />
              </button>
            </div>

            {/* ── Ticket context chip ── */}
            {ticket && (
              <div style={{
                margin: "12px 14px 0",
                background: `rgba(${ticket.days_unresponded >= 14 ? "220,38,38" : "245,158,11"},0.07)`,
                border: `1px solid rgba(${ticket.days_unresponded >= 14 ? "220,38,38" : "245,158,11"},0.22)`,
                borderRadius: 9, padding: "9px 12px", flexShrink: 0,
              }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: daysBadgeColor, marginBottom: 3 }}>
                  ⚠ Unresponded {ticket.days_unresponded} day{ticket.days_unresponded !== 1 ? "s" : ""}
                </div>
                <div style={{ fontSize: 12.5, color: textMain, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {ticket.title}
                </div>
                <div style={{ fontSize: 11, color: textMute, marginTop: 2 }}>
                  {ticket.status} · {ticket.priority} · {ticket.queue}
                </div>
              </div>
            )}

            {/* ── Messages ── */}
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>

              {/* Initializing skeleton */}
              {initializing && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(99,102,241,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                    <FaRobot size={11} color={accent} />
                  </div>
                  <div style={{
                    background: surface, border: `1px solid ${border}`,
                    borderRadius: "12px 12px 12px 3px", padding: "10px 14px",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    {[0, 0.18, 0.36].map((d, i) => (
                      <motion.div key={i}
                        animate={{ opacity: [0.3, 1, 0.3], y: [0, -4, 0] }}
                        transition={{ duration: 0.9, repeat: Infinity, delay: d }}
                        style={{ width: 6, height: 6, borderRadius: "50%", background: accent }}
                      />
                    ))}
                    <span style={{ fontSize: 12, color: textMute, marginLeft: 4 }}>Analyzing ticket…</span>
                  </div>
                </div>
              )}

              {/* Message bubbles */}
              {messages.map((msg, i) => (
                <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", alignItems: "flex-start", gap: 8 }}>
                  {msg.role === "ai" && (
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(99,102,241,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                      <FaRobot size={11} color={accent} />
                    </div>
                  )}
                  <div style={{
                    maxWidth: "84%",
                    background: msg.role === "user"
                      ? `linear-gradient(135deg, ${accent}, #8b5cf6)`
                      : msg.isError ? "rgba(239,68,68,0.07)"
                      : surface,
                    color: msg.role === "user" ? "#fff"
                      : msg.isError ? "#dc2626"
                      : textMain,
                    borderRadius: msg.role === "user" ? "13px 13px 3px 13px" : "13px 13px 13px 3px",
                    padding: "9px 13px",
                    fontSize: 13,
                    lineHeight: 1.58,
                    border: msg.role === "ai" && !msg.isError ? `1px solid ${border}` : "none",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}>
                    {renderText(msg.text)}
                  </div>
                </div>
              ))}

              {/* Sending indicator */}
              {sending && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(99,102,241,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                    <FaRobot size={11} color={accent} />
                  </div>
                  <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: "12px 12px 12px 3px", padding: "10px 14px", display: "flex", gap: 4, alignItems: "center" }}>
                    {[0, 0.18, 0.36].map((d, i) => (
                      <motion.div key={i} animate={{ opacity: [0.3, 1, 0.3], y: [0, -4, 0] }} transition={{ duration: 0.9, repeat: Infinity, delay: d }}
                        style={{ width: 6, height: 6, borderRadius: "50%", background: accent }} />
                    ))}
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* ── Input ── */}
            <div style={{
              padding: "10px 14px 16px",
              borderTop: `1px solid ${border}`,
              display: "flex", gap: 8, alignItems: "flex-end",
              flexShrink: 0,
              background: isDark ? "rgba(255,255,255,0.01)" : "#fafafe",
            }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder={initializing ? "Waiting for analysis…" : "Ask a follow-up question… (Enter to send)"}
                rows={1}
                disabled={sending || initializing}
                style={{
                  flex: 1, resize: "none",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#ddddf0"}`,
                  borderRadius: 10,
                  padding: "8px 11px",
                  fontSize: 13, fontFamily: "inherit",
                  color: textMain,
                  background: isDark ? "rgba(255,255,255,0.04)" : "#fff",
                  outline: "none", lineHeight: 1.5,
                  minHeight: 38, maxHeight: 96, overflowY: "auto",
                  opacity: (sending || initializing) ? 0.6 : 1,
                  transition: "border-color 0.15s, opacity 0.15s",
                }}
                onFocus={e => { e.target.style.borderColor = accent; }}
                onBlur={e => { e.target.style.borderColor = isDark ? "rgba(255,255,255,0.1)" : "#ddddf0"; }}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || sending || initializing}
                style={{
                  width: 38, height: 38, borderRadius: 10, border: "none",
                  background: input.trim() && !sending && !initializing ? accent : "rgba(99,102,241,0.22)",
                  color: "#fff",
                  cursor: input.trim() && !sending && !initializing ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, transition: "background 0.15s",
                }}
              >
                {sending
                  ? <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}>
                      <FaSpinner size={13} />
                    </motion.div>
                  : <FaPaperPlane size={13} />
                }
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ChatDrawer;